import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
// NPM
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
// Libs
import { ViewportChangeService } from '@libs/services/viewport-change';
// Local
import { MultiFilterTypeaheadConfig } from '../filters/multi-filter-typeahead/multi-filter-typeahead-config';
import { MultiFilterTypeaheadLoadComplete } from '../filters/multi-filter-typeahead/multi-filter-typeahead-load-complete';
import { SearchFilterState } from './search-filter-state';
import { IMultiFilter } from '../../shared/types';
import {
  ICheckboxListFilter,
  IDateRangeFilter,
  IRadioButtonListFilter,
  ITextInputFilter,
  ITypeaheadFilter,
  MultiFilterType,
} from '../../shared/types/interfaces';
import {
  IFilterControl,
  ConfiguredControl,
} from '../../types/internal-interfaces';
import { CheckboxListConfig } from '../filters/checkbox-list/checkbox-list-config';
import { TextInputConfig } from '../filters/text-input/text-input-config';
import { DateRangeConfig } from '../filters/date-range/date-range-config';
import { RadioButtonListConfig } from '../filters/radio-button-list/radio-button-list-config';

@Component({
  selector: 'search-filter',
  templateUrl: 'search-filter.component.html',
  styleUrls: ['search-filter.component.scss'],
})
export class SearchFilterComponent implements OnInit, OnChanges {
  @Input() accountId: string;
  @Input() filters: IMultiFilter[];
  @Output() filterUpdated: EventEmitter<object> = new EventEmitter();
  @Output() hasOneItem: EventEmitter<string> = new EventEmitter(null);

  public multiFilterType = MultiFilterType;
  public hideFilter: boolean = true;
  public hideSubmit: boolean = true;
  public isLocationsLoading: boolean = true;
  public resultSnapshot: object;
  public filterState = new SearchFilterState();

  public innerWindowHeight: number;

  constructor(
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private viewportChange: ViewportChangeService,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  ngOnInit(): void {
    this.initializeResizeListener();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const filters = changes.filters?.currentValue ?? this.filters;
    const accountId = changes.accountid?.currentValue ?? this.accountId;
    if (filters && accountId) {
      this.filterState.load(filters);
      // take a snapshot of the initial values
      // if resultSnapshot isn't null, it was called before everything was initialized
      const results = this.resultSnapshot ?? this.filterState.getResults();
      this.resultSnapshot = results;
    }
  }

  // waits for drawer animation to finish before hiding itself
  public startDelayedDrawerClose(): void {
    this.document.body.classList.remove('no-scroll');
    setTimeout(() => {
      this.hideFilter = true;
    }, 150);
  }

  public setFilters(filters: Object): void {
    this.resultSnapshot = filters;
    this.filterState.loadFromResults(this.resultSnapshot);
    this.cdr.detectChanges();
  }

  public drawerOpening(): void {
    this.document.body.classList.add('no-scroll');
    if (this.resultSnapshot) {
      // When the drawer opens rollback selections to the previous snapshot
      // This will clear out the unsaved changes
      this.filterState.loadFromResults(this.resultSnapshot);
      this.hideSubmit = true;
    }
  }

  public setNewFilters(): void {
    // Take a snapshot of the user's selections
    const results = this.filterState.getResults();
    this.resultSnapshot = results;

    // Notify subscribers of results
    this.filterUpdated.emit(results);
  }

  public errorOccured(message: string): void {
    this.snackBar.open(message, null, { panelClass: 'error', duration: 3000 }); // open fail snack bar
  }

  public loadComplete(
    filter: IMultiFilter,
    loadComplete: MultiFilterTypeaheadLoadComplete,
  ): void {
    if (loadComplete.hasOneOption) {
      this.hasOneItem.emit(filter.id);
    }

    this.isLocationsLoading = false; // fix this at some point
  }

  public getTypeaheadConfig(
    filter: ITypeaheadFilter,
    viewControl: IFilterControl,
  ): MultiFilterTypeaheadConfig {
    return this.addOrGetConfig(filter.id, viewControl, () => {
      const config: MultiFilterTypeaheadConfig = {
        labelText: filter.headingText?.toLowerCase(),
        accountId: this.accountId,
        headingText: filter.headingText,
        isExpanded: filter.isExpanded,
        typeaheadService: filter.typeaheadService,
      };
      return config;
    });
  }

  public getTextInputConfig(
    filter: ITextInputFilter,
    viewControl: IFilterControl,
  ): TextInputConfig {
    return this.addOrGetConfig(filter.id, viewControl, () => {
      const config: TextInputConfig = {
        inputHint: filter.inputHint,
        inputLabel: filter.inputLabel,
      };
      return config;
    });
  }

  public getCheckboxListConfig(
    filter: ICheckboxListFilter,
    viewControl: IFilterControl,
  ): CheckboxListConfig {
    return this.addOrGetConfig(filter.id, viewControl, () => {
      const config: CheckboxListConfig = {
        options: [...filter.options],
      };
      return config;
    });
  }

  public getRadioButtonListConfig(
    filter: IRadioButtonListFilter,
    viewControl: IFilterControl,
  ): CheckboxListConfig {
    return this.addOrGetConfig(filter.id, viewControl, () => {
      const config: RadioButtonListConfig = {
        options: [...filter.options],
        defaultSelection: filter.defaultOption,
      };
      return config;
    });
  }

  public getDateRangeConfig(
    filter: IDateRangeFilter,
    viewControl: IFilterControl,
  ): DateRangeConfig {
    return this.addOrGetConfig(filter.id, viewControl, () => {
      const config: DateRangeConfig = {
        datePresets: [...filter.presetDates],
      };
      return config;
    });
  }

  public updateFilters(filter: IMultiFilter, selectedFilters: object): void {
    this.filterState.setResult(filter.id, selectedFilters);
    this.hideSubmit = false;
  }

  public updateSelectedOptionCount(filter: IMultiFilter, count: number): void {
    this.filterState.setSelectedOptionsCount(filter.id, count);
  }

  private addOrGetConfig<R>(
    filterId: string,
    viewControl: IFilterControl,
    createConfig: () => R,
  ): R {
    //This method gets called a lot so we shouldn't construct new objects each time
    // try to get control from cache.
    let cachedControl: ConfiguredControl =
      this.filterState.getControl(filterId);

    if (!cachedControl) {
      const config = createConfig();

      cachedControl = {
        filterControl: viewControl,
        config: config,
      };
      this.filterState.registerControl(filterId, cachedControl);
    }

    return cachedControl.config;
  }

  private initializeResizeListener(): void {
    this.viewportChange
      .getInnerHeight()
      .subscribe((innerWindowHeight: number) => {
        this.innerWindowHeight = innerWindowHeight;
      });
  }
}
